import { createTheme, radioClasses } from "@mui/material";
import { boxShadows, colours } from '../StyleConstants';
import React from "react";
import {
    buttonBaseVariant, buttonDisabled, buttonHighlightVariant, buttonOutlinedVariant,
    buttonPrimaryVariant,
    buttonSecondaryVariant,
    buttonSizes, buttonTertiaryVariant,
} from './buttons';

export const theme = createTheme({
    breakpoints: {
        values: {
            xs: 300, // phone
            sm: 600, // tablets
            md: 900, // small laptop
            lg: 1200, // desktop
            xl: 1536 // large screens
        }
    },
    typography: {
        fontFamily: "Libre Franklin",
        allVariants: {
            color: colours.midnightBlue.blue
        }
    },
    palette: {
        primary: {
            light: colours.core.white,
            main: "#3d3368",
            dark: colours.midnightBlue.blue160,
        },
    },
    /**
     * Due to a bug in MUI we have to use this function instead of the array like this:
     * spacing: [0, 4, 8, 16, 24, 32, 64, 128, 256, 512, 1024],
     *
     * Source:
     * https://github.com/mui/material-ui/issues/29677
     *
     * Reason:
     * Some components will use non-integer values for spacing and this breaks the spacing array.
     *
     * Solution:
     * Use a function instead of an array and calculate the spacing based on the factor
     *
     * Result:
     * theme.spacing(1)   // 4px
     * theme.spacing(1.5) // 6px
     * theme.spacing(2)   // 8px
     * theme.spacing(2.5) // 12px
     */
    spacing: (factor: number) => {
        const values = [0, 4, 8, 16, 32, 64];
        const index = Math.floor(factor);
        const currentSpace = values[index];
        const nextSpace = values[index + 1] || currentSpace * 2;
        const space = currentSpace + (nextSpace - currentSpace) * (factor - index);
        return `${space}px`;
    },
    // spacing: [0, 4, 8, 16, 32, 64],
    components: {
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    background: "#f5f5f5",
                    borderBottom: "0.5px solid #c0c2c6",
                    fontFamily: "Libre Franklin Bold",
                    padding: "8px 16px",
                    fontSize: "18px",
                    boardRadius: "20px",
                    // color: '#f5f5f5'
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    margin: "4px",
                    borderRadius: "18px",
                    boxShadow: "0px 3px 5px -1px rgb(0 0 0 / 20%)",
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    "&:last-child": {
                        paddingBottom: "0px",
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '8px',
                    "&:hover": {
                        boxShadow: "1px 4px 8px 2px rgba(21, 24, 24, 0.25)",
                    },
                    "&:focus": {
                        outline: `2px solid ${colours.notifications.info50}`
                    }
                },
            },
            defaultProps: {
                disableRipple: true,
                disableFocusRipple: true,
            },
            variants: [
                ...buttonSizes,
                ...buttonBaseVariant,
                ...buttonPrimaryVariant,
                ...buttonSecondaryVariant,
                ...buttonTertiaryVariant,
                ...buttonOutlinedVariant,
                ...buttonHighlightVariant,
                ...buttonDisabled,
            ]
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    border: `2px solid transparent`,
                    "&:focus": {
                        border: `2px solid ${colours.notifications.info50}`,
                    }
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: colours.orange.orange,
                    [`&.${radioClasses.checked}`]: {
                        color: colours.orange.orange,
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    backgroundColor: "white",
                    borderRadius: "5px",
                    boxShadow: "1px 2px 5px 0px rgba(21, 24, 24, 0.10)"
                }
            }
        },

        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    border: "none"
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: boxShadows.levelOne
                }
            }
        }
    },
});
declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        base: true;
        highlight: true;
        primary: true;
        secondary: true;
        tertiary: true;
    }
}
declare module '@mui/material/styles' {
    interface TypographyVariants {
        body3: React.CSSProperties;
    }
    interface TypographyVariantsOptions {
        body3?: React.CSSProperties;
    }
}
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body3: true;
    }
}